import React, { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import styles from 'src/app/projects/views/new-project-definition/NewProjectDefinition.module.scss';
import SelectMultiple from 'src/shared/components/select-multiple/SelectMultiple';
import { countries } from 'src/shared/data/countries';
import noop from 'lodash/noop';
import Input from 'src/shared/components/input/Input';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useProjectDefinition from 'src/app/projects/views/new-project-definition/hooks/useProjectDefinition';
import { useCustomerProjectsInBCQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { FeatureType } from 'src/shared/types';
import { Search, Info } from 'lucide-react';
import { hasInsightsFeature, hasHabitatInsightsFeature } from 'src/shared/utils/featureHelpers';

export const BasicInformation = () => {
    const { formState, isEditMode, onFormChange } = useProjectDefinition();
    const { currentCustomerId, currentCustomerName, currentCustomerDetails } = useCustomer();
    const displayCustomerName = currentCustomerDetails?.customerName || currentCustomerName;
    const [projectNameSearchText, setProjectNameSearchText] = useState(formState.projectName || '');
    const [debouncedProjectSearch, setDebouncedProjectSearch] = useState('');
    const [countrySearchText, setCountrySearchText] = useState(formState.country || '');

    // Sync search text with form state, especially important for edit mode
    useEffect(() => {
        setProjectNameSearchText(formState.projectName || '');
    }, [formState.projectName]);

    useEffect(() => {
        setCountrySearchText(formState.country || '');
    }, [formState.country]);

    const debouncedSetProjectSearch = useCallback(
        debounce((value: string) => {
            setDebouncedProjectSearch(value);
        }, 300),
        []
    );

    const handleProjectNameInputChange = (value: string) => {
        setProjectNameSearchText(value);
        debouncedSetProjectSearch(value);
    };

    const handleCountryInputChange = (value: string) => {
        setCountrySearchText(value);
        // If the input is cleared (empty), update the form state
        if (value === '') {
            onFormChange({ country: '' });
        }
    };

    const { data: projectsData } = useCustomerProjectsInBCQuery(
        {
            input: {
                customerId: currentCustomerId,
                searchText: debouncedProjectSearch,
                limit: 10,
            },
        },
        {
            skip: !currentCustomerId || debouncedProjectSearch.length < 1,
        }
    );

    /**
     * Arriving with a projectId in the URL (from the admin projects list) means the project
     * already exists in the graph but has no definition yet. Look it up so its name, code and
     * lead contact are filled in rather than making the user re-select the project they just
     * chose. Only runs while the form is still blank, so it never overwrites user input.
     */
    const { data: namedProjectData } = useCustomerProjectsInBCQuery(
        { input: { customerId: currentCustomerId, searchText: '' } },
        { skip: !currentCustomerId || !formState.projectId || Boolean(formState.projectName) }
    );

    useEffect(() => {
        if (!formState.projectId || formState.projectName) {
            return;
        }

        const namedProject = namedProjectData?.customerProjectsInBC?.projects?.find(entry => entry?.projectId === formState.projectId);

        if (!namedProject?.projectName) {
            return;
        }

        onFormChange({
            projectName: namedProject.projectName,
            projectCode: namedProject.projectCode || 'Not set',
            projectLeadName: namedProject.leadName || 'Not set',
            projectLeadEmail: namedProject.leadEmail || 'Not set',
        });
    }, [namedProjectData, formState.projectId, formState.projectName, onFormChange]);

    const featureOptions = [
        { value: FeatureType.EDNA, label: 'Species Insights' },
        { value: FeatureType.HLCA, label: 'Habitat Insights' },
    ];

    const onProjectChange = (projectName: string | null) => {
        if (!projectName) {
            return;
        }

        const selectedProjectData = projectsData?.customerProjectsInBC.projects.find(entry => entry.projectName === projectName);

        if (!selectedProjectData) {
            return;
        }

        onFormChange({
            projectId: selectedProjectData.projectId,
            projectName: projectName,
            projectCode: selectedProjectData.projectCode || 'Not set',
            projectLeadEmail: selectedProjectData.leadEmail || 'Not set',
            projectLeadName: selectedProjectData.leadName || 'Not set',
        });
    };

    const onFeaturesChange = (features: string[]) => {
        onFormChange({
            features: features as FeatureType[],
        });
    };

    // The autocomplete only shows a value that is among its options, and the options come
    // from the search query — which stays idle until the user types. Include the already
    // selected project so a prefilled name is visible rather than appearing blank.
    const projectNameOptions = useMemo(() => {
        const searched = (projectsData?.customerProjectsInBC?.projects || []).map(entry => entry.projectName);
        return formState.projectName && !searched.includes(formState.projectName) ? [formState.projectName, ...searched] : searched;
    }, [projectsData, formState.projectName]);

    const featureSelectionFeedback = useMemo(() => {
        const selectedFeatures = formState.features || [];
        const hasEDNA = hasInsightsFeature(selectedFeatures);
        const hasHLCA = hasHabitatInsightsFeature(selectedFeatures);

        if (selectedFeatures.length === 0) {
            return null;
        }

        const messages: string[] = [];
        if (hasEDNA) {
            messages.push('Project Aims and Sampling Cadence sections will be enabled');
        }
        if (hasHLCA) {
            messages.push('Habitat Insights section will be enabled');
        }
        if (selectedFeatures.includes(FeatureType.SURVEY)) {
            messages.push('Survey functionality will be enabled');
        }

        return messages.length > 0 ? messages.join('. ') + '.' : null;
    }, [formState.features]);

    if (!displayCustomerName) {
        return null;
    }

    return (
        <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Basic information</div>
            <div className={styles.formInputSection}>
                <div className={styles.formInputRow}>
                    <div className={styles.formInputField}>
                        <div className={styles.formInputSectionTitle}>Customer name</div>
                        <Input value={displayCustomerName} onChange={noop} disabled={true} />
                    </div>
                    <div className={styles.formInputField}>
                        <div className={styles.formInputSectionTitle}>
                            Project name <span className={styles.requiredIndicator}>*</span>
                        </div>
                        <Autocomplete
                            options={['', ...projectNameOptions]}
                            placeholder='Search project name'
                            prefixIcon={<Search />}
                            onChange={onProjectChange}
                            onInputChange={handleProjectNameInputChange}
                            inputValue={projectNameSearchText}
                            value={formState.projectName}
                            disabled={isEditMode}
                            disableClientFilter={true}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputRow}>
                    <div className={styles.formInputField}>
                        <div className={styles.formInputSectionTitle}>Project code</div>
                        <Input value={formState.projectCode} onChange={noop} disabled={true} />
                    </div>

                    <div className={styles.formInputField}>
                        <div className={styles.formInputSectionTitle}>Features</div>
                        <SelectMultiple
                            className={styles.selectContainer}
                            options={featureOptions}
                            values={formState.features}
                            onChange={onFeaturesChange}
                            width='100%'
                            noSelectionText='No features selected (Starter)'
                            formatSelectedValues={count => `${count} feature${count > 1 ? 's' : ''} selected`}
                        />
                        {featureSelectionFeedback && (
                            <div className={styles.featureFeedback}>
                                <Info size={16} />
                                <span>{featureSelectionFeedback}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.formInputSection}>
                <div className={styles.formInputRow}>
                    <div className={styles.formInputField}>
                        <div className={styles.formInputSectionTitle}>
                            Sampling country <span className={styles.requiredIndicator}>*</span>
                        </div>
                        <Autocomplete
                            options={['', ...Object.keys(countries).sort()]}
                            placeholder='Search country name'
                            prefixIcon={<Search />}
                            onChange={value => {
                                const countryValue = value === null || value === '' ? '' : value;
                                onFormChange({ country: countryValue });
                            }}
                            value={formState.country || ''}
                            onInputChange={handleCountryInputChange}
                            inputValue={countrySearchText}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInformation;
